/**
 * @file ArchiveSettingsModal.js
 * @description Modal component for configuring video archive settings
 */

import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";

const ArchiveSettingsModal = ({ isOpen, onClose, onSave }) => {
  const [videoId, setVideoId] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [shuffledIntervals, setShuffledIntervals] = useState([]);
  const summaryLabels = {
  B: "Bullet summary",
  N: "No summary",
  P: "Paragraph summary"
};

  // Fetch and validate video ID when it changes
  useEffect(() => {
    const validateVideo = async () => {
      if (videoId.trim() === "") {
        setValidationMessage("");
        setShuffledIntervals([]);
        return;
      }

      setIsValidating(true);
      setValidationMessage("Validating video...");

      try {
        const videoRef = doc(db, "videos", videoId.trim());
        const videoSnap = await getDoc(videoRef);

        if (videoSnap.exists()) {
          const data = videoSnap.data();
          if (data.videoURL || data.title) {
            setValidationMessage("✅ Video found in database");
            
            // Get the original intervals and summary order from the video document
            const rangesFromDb = data.time_ranges || [];
            const summaryOrder = [
              data.summary1,
              data.summary2,
              data.summary3
            ];

            // a randomized array of indices
            const shuffledRanges = [...rangesFromDb].sort(() => Math.random() - 0.5);

            // the shuffled ranges with the fixed summary order
            const newShuffledIntervals = shuffledRanges.map((range, index) => ({
              ...range,
              summaryType: summaryOrder[index] || "N", // default to "N" 
            }));

            setShuffledIntervals(newShuffledIntervals);

          } else {
            setValidationMessage("❌ The video exists, but the required data is missing.");
          }
        } else {
          setValidationMessage("❌ Video not found in database");
        }
      } catch (error) {
        console.error("Error while checking video:", error);
        setValidationMessage("⚠️ Error while verifying video");
      } finally {
        setIsValidating(false);
      }
    };

    const timeoutId = setTimeout(validateVideo, 500); // Debounce validation
    return () => clearTimeout(timeoutId);
  }, [videoId]);

  // Reset validation when modal closes
  useEffect(() => {
    if (!isOpen) {
      setValidationMessage("");
      setVideoId("");
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!videoId.trim()) {
      alert("Please enter the video ID");
      return;
    }

    if (validationMessage.includes("❌")) {
      alert("Please enter a valid video ID");
      return;
    }
    
    // Ensure that intervals were successfully generated before saving
    if (shuffledIntervals.length === 0) {
      alert("No valid intervals found for this video. Unable to save.");
      return;
    }

    try {
      // Update the teamSync document in Firebase
      const teamVideoRef = doc(db, "teamSync", "currentVideo");
      await updateDoc(teamVideoRef, {
        videoId: videoId.trim(),
        // Save the shuffled intervals with their assigned summary types to the correct field
        time_ranges: shuffledIntervals,
        changedBy: "Admin", // Optional: to track who made the change
        timestamp: new Date(),
      });
      console.log("Archive settings saved successfully!");

      onClose();
    } catch (error) {
      console.error("Error saving archive settings:", error);
      alert("Error saving settings. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-base-100 rounded-lg p-6 w-96 max-w-lg shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Archive settings</h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm btn-circle">
            ✕
          </button>
        </div>

        <div className="space-y-4">
          {/* Video ID Input */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">video ID</span>
            </label>
            <input
              type="text"
              value={videoId}
              onChange={(e) => setVideoId(e.target.value)}
              className={`input input-bordered w-full ${
                validationMessage.includes("❌")
                  ? "input-error"
                  : validationMessage.includes("✅")
                    ? "input-success"
                    : ""
              }`}
            />
            {(validationMessage || isValidating) && (
              <div
                className={`text-sm mt-1 ${
                  validationMessage.includes("❌")
                    ? "text-error"
                    : validationMessage.includes("✅")
                      ? "text-success"
                      : "text-warning"
                }`}
              >
                {isValidating ? "Перевірка..." : validationMessage}
              </div>
            )}
          </div>
          
          {shuffledIntervals.length > 0 && (
            <div className="form-control">
              <label className="label">
                <span className="label-text">Generated playback order</span>
              </label>
              <div className="space-y-2">
                {shuffledIntervals.map((interval, index) => (
                  <div key={index} className="flex justify-between items-center bg-base-200 p-2 rounded-md">
                    <span>{`From ${interval.startTime} to ${interval.endTime}`}</span>
                    <span
  className={`badge ${
    interval.summaryType === "P"
      ? "badge-primary"
      : interval.summaryType === "B"
      ? "badge-info"
      : "badge-ghost"
  }`}
>
  {summaryLabels[interval.summaryType] || interval.summaryType}
</span>

                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="modal-action">
          <button onClick={onClose} className="btn btn-ghost">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isValidating || validationMessage.includes("❌") || shuffledIntervals.length === 0}
            className="btn btn-primary"
          >
            Save settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArchiveSettingsModal;